import * as React from "react";
import { cva } from "class-variance-authority";
import { AlertTriangleIcon } from "lucide-react";
import { Button } from "./button";
import { cn } from "@gerama/utils";

export type StateVariant = "panel" | "immersive";

/**
 * `panel` — compact bordered card for the admin operator panel.
 * `immersive` — the public site's Spotify-style full-bleed state: no card
 * chrome, a large icon badge, a bold heading and a brand pill action.
 */
export const stateRootVariants = cva("flex flex-col items-center justify-center text-center", {
  variants: {
    variant: {
      panel: "gap-3 rounded-lg border py-16",
      immersive: "gap-6 px-6 py-20",
    },
  },
  defaultVariants: { variant: "panel" },
});

export const stateIconBadgeVariants = cva("", {
  variants: {
    variant: {
      panel: "[&_svg]:size-6",
      immersive:
        "grid size-20 place-items-center rounded-full [&_svg]:size-9 [&_svg]:stroke-[1.75]",
    },
  },
  defaultVariants: { variant: "panel" },
});

export const stateTitleVariants = cva("", {
  variants: {
    variant: {
      panel: "font-medium",
      immersive: "text-2xl font-bold tracking-tight text-balance",
    },
  },
  defaultVariants: { variant: "panel" },
});

export const stateDescriptionVariants = cva("text-muted-foreground text-sm", {
  variants: {
    variant: {
      panel: "",
      immersive: "mx-auto max-w-sm text-pretty",
    },
  },
  defaultVariants: { variant: "panel" },
});

/** Spotify-style pill: bold, slightly grows on hover, settles on press. */
export const statePillClassName =
  "h-12 rounded-full px-8 text-base font-bold transition-transform duration-150 hover:scale-[1.04] hover:bg-primary hover:brightness-110 active:scale-100";

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  variant?: StateVariant;
  className?: string;
}

/** The `error` state of the loading -> error -> empty -> content machine (skill 9.1) — always offers Retry. */
export function ErrorState({
  title = "مشکلی پیش آمد",
  description,
  onRetry,
  variant = "panel",
  className,
}: ErrorStateProps) {
  const immersive = variant === "immersive";
  return (
    <div
      role="alert"
      className={cn(
        stateRootVariants({ variant }),
        !immersive && "border-destructive/30 bg-destructive/5",
        className,
      )}
    >
      <div
        className={cn(
          stateIconBadgeVariants({ variant }),
          immersive ? "bg-destructive/15 text-destructive" : "text-destructive",
        )}
      >
        <AlertTriangleIcon aria-hidden />
      </div>
      <div className={immersive ? "space-y-2" : "space-y-1"}>
        <p className={stateTitleVariants({ variant })}>{title}</p>
        {description && <p className={stateDescriptionVariants({ variant })}>{description}</p>}
      </div>
      {onRetry &&
        (immersive ? (
          <Button onClick={onRetry} className={statePillClassName}>
            تلاش دوباره
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={onRetry}>
            تلاش دوباره
          </Button>
        ))}
    </div>
  );
}
